import { Alert, Snackbar, Theme } from "@mui/material";
import { SyntheticEvent } from "react";
import { radius } from "src/design/tokens";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import {
  selectSnackbar,
  showSnackbar,
} from "src/store/reducers/navigation/navigationSlice";

type ToastType = "success" | "error" | "warning" | "info";

const toneColors = (theme: Theme, type: ToastType) => {
  const { palette } = theme;
  switch (type) {
    case "error":
      return {
        bg: palette.dangerSoft,
        fg: palette.dangerInk,
        star: palette.dangerInk,
      };
    case "warning":
      return {
        bg: palette.tokens.warningBg,
        fg: palette.tokens.warningFg,
        star: palette.tokens.warningFg,
      };
    case "info":
      return {
        bg: palette.tokens.primarySoft,
        fg: palette.primaryInk,
        star: palette.primaryInk,
      };
    default:
      return { bg: palette.strong, fg: palette.onStrong, star: palette.gold };
  }
};

/** Bottom-centered toast pill (design `hasToast`): strong fill, gold star, 700/14 text. */
const AppSnackbar = () => {
  const { open, message, type } = useAppSelector(selectSnackbar);
  const dispatch = useAppDispatch();
  const tone: ToastType = type || "success";

  const handleClose = (_event?: Event | SyntheticEvent, reason?: string) => {
    if (reason === "clickaway") return;
    dispatch(showSnackbar({ open: false, message: "", type: "warning" }));
  };

  return (
    <Snackbar
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      open={open}
      autoHideDuration={4000}
      onClose={handleClose}
      sx={{ bottom: { xs: 16, sm: 28 } }}
    >
      <Alert
        onClose={() => handleClose()}
        severity={tone}
        variant="standard"
        role={tone === "error" || tone === "warning" ? "alert" : "status"}
        icon={
          tone === "success" ? (
            <span aria-hidden="true" data-testid="toast-star">
              ★
            </span>
          ) : undefined
        }
        sx={(theme) => {
          const { bg, fg, star } = toneColors(theme, tone);
          return {
            width: "100%",
            maxWidth: "calc(100vw - 40px)",
            alignItems: "center",
            borderRadius: `${radius.pill}px`,
            fontSize: 14,
            fontWeight: 700,
            px: 2.5,
            py: 0.75,
            backgroundColor: bg,
            color: fg,
            boxShadow: theme.shadows[8],
            "& .MuiAlert-icon": { color: star, opacity: 1, fontSize: 16 },
            "& .MuiAlert-action": { color: fg },
          };
        }}
      >
        {message}
      </Alert>
    </Snackbar>
  );
};

export default AppSnackbar;
